import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateOrder } from 'api/orders/use-update-order.ts';
import type { TOrderRes, TUpdateOrderReq } from 'api/swagger';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import { EFormDrawerWithButtonIconType, FormDrawerWithButton } from 'components/common/form-drawer-with-button';
import type { IOrderFormFields } from 'components/orders/order-form';
import { OrderForm } from 'components/orders/order-form';
import { OrderFormConclusion } from 'components/orders/order-form/components/order-form-conclusion';

interface IOrderEdit {
  order: TOrderRes;
}

export const OrderEdit: React.FC<IOrderEdit> = ({ order }) => {
  const isAllow = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.accountant, UserRole.customer_manager]);
  const { t } = useTranslation();
  const { mutateAsync: updateOrder, isLoading } = useUpdateOrder(order.id);

  const handleFinish = async (values: IOrderFormFields) => {
    const apiVariables: TUpdateOrderReq = {
      clientOrderNumber: values.clientOrderNumber,
      contactId: values.contact.value,
      currency: values.currency,
      freight: values.freight,
      fine: values.fine,
      documentPrice: values.documentPrice,
      paymentTerms: values.paymentTerms,
      paymentPeriod: values.paymentPeriod,
    };

    await updateOrder(apiVariables);
  };

  const initialValues = {
    client: {
      value: order.client.id,
      label: order.client.shortName || order.client.name,
    },
    contact: {
      value: order.contact.id,
      label: order.contact.name,
    },
    clientOrderNumber: order.clientOrderNumber,
    currency: order.currency,
    freight: order.freight,
    fine: order.fine,
    documentPrice: order.documentPrice,
    paymentTerms: order.paymentTerms,
    paymentPeriod: order.paymentPeriod,
  };

  if (!isAllow) return null;

  return (
    <FormDrawerWithButton
      formComponent={OrderForm}
      formProps={{
        initialValues,
        withFine: true,
        disableClientInput: true,
        footerRender: (client, contact) => <OrderFormConclusion client={client} contact={contact} />,
      }}
      title={t('order:edit_order')}
      buttonLabel={t('order:edit_order')}
      iconType={EFormDrawerWithButtonIconType.EDIT}
      isLoading={isLoading}
      onFinish={handleFinish}
    />
  );
};
